import { useEffect } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { TariffCustomerCard } from "@/features/billing/TariffCustomerCard";
import { getTariffCustomer, tariffKeys } from "@/features/billing/tariffs.api";
import { firstAllowedRoute, hasModule } from "@/lib/auth";
import { mayReadTariffs } from "@/features/billing/access";

// Commercie → Tarieven → a customer: its price agreement, one version at a time, changed in a concept and approved by
// a second person (docs/tms-facturatie.md §24.10). `version` in the address is the one shown.
export const Route = createFileRoute("/_shell/billing/tariffs/customers/$partyId")({
  beforeLoad: ({ context }) => {
    if (!mayReadTariffs(context.session)) throw redirect({ to: firstAllowedRoute(context.session) });
  },
  validateSearch: (search: Record<string, unknown>): { version?: string } => ({
    version: typeof search.version === "string" && /^[0-9a-f-]{36}$/.test(search.version) ? search.version : undefined,
  }),
  component: TariffCustomerScreen,
});

function TariffCustomerScreen() {
  const { t } = useTranslation();
  const { partyId } = Route.useParams();
  const { session } = Route.useRouteContext();
  const { version } = Route.useSearch();
  const navigate = Route.useNavigate();
  const admin = session.roles.includes("admin");
  const query = useQuery({ queryKey: tariffKeys.customer(partyId), queryFn: () => getTariffCustomer(partyId), retry: false });
  // A link to one method (#method-…, from a tariff or the order card) scrolls to it once it is drawn.
  useEffect(() => {
    if (!query.data || !window.location.hash) return;
    document.getElementById(window.location.hash.slice(1))?.scrollIntoView({ block: "start" });
  }, [query.data]);
  return (
    <div className="wl-page" data-screen-label="Back-office Tariff Customer">
      {query.error ? (
        <div className="wl-alert wl-alert--danger" role="alert">
          {query.error instanceof Error ? query.error.message : String(query.error)}
        </div>
      ) : query.data ? (
        <TariffCustomerCard
          data={query.data}
          userId={session.userId}
          canWrite={admin || hasModule(session, "price_agreements_write")}
          canApprove={admin || hasModule(session, "price_agreements_approve")}
          versionId={version ?? null}
          onVersion={(id) => navigate({ search: { version: id ?? undefined }, replace: true })}
        />
      ) : (
        <span className="t-caption t-muted">{t("common.loading")}</span>
      )}
    </div>
  );
}
